import { createReducer, on } from '@ngrx/store';
import { IUserInfoModel } from '@nx-fe/services';
import { SupportRequest, SupportRequestsDashboardData, SupportRequestsFilterOptions } from '../models';
import { SupportActions } from './actions';
import { SupportRequestStatus } from '../enums';

/**
 * State for the support feature.
 */
export interface SupportState {
  /**
   * Current user Id
   */
  currentUserId: string;

  /**
   * Indicates whether the support feature has been initialized.
   */
  isInitialized: boolean;
  /**
   * Data for the support requests dashboard.
   */
  dashboardData: SupportRequestsDashboardData | undefined | null;
  /**
   * The date when the support requests data was last updated.
   */
  lastUpdated: number | undefined;
  /**
   * The support request that has been last changed.
   */
  supportRequestChanged: SupportRequest;
  /**
   * Assignees for groups.
   * Key: group ID.
   */
  groupAssignees: { [id: string]: IUserInfoModel[] };

  /**
   * Support requests assigned to current user
   */
  assignedSupportRequests: SupportRequest[];
  /**
   * Support
   */
  showingNotification: ISupportNotification;
  /**
   * Support request data load filter
   */
  supportRequestFilterOption: SupportRequestsFilterOptions;
  /**
   * Support request chart toggle
   */
  supportRequestChartToggle: boolean;
}

/**
 * Initial state for the support feature.
 */
export const initialSupportState: SupportState = {
  currentUserId: undefined,
  isInitialized: false,
  dashboardData: undefined,
  lastUpdated: undefined,
  supportRequestChanged: undefined,
  groupAssignees: {},
  assignedSupportRequests: [],
  showingNotification: {
    supportRequests: [],
    ringingSupportRequestIds: []
  },
  supportRequestFilterOption: undefined,
  supportRequestChartToggle: true
};

export interface ISupportNotification {
  supportRequests: SupportRequest[];
  ringingSupportRequestIds: number[];
}

/**
 * Reducer for the support feature.
 */
export const supportReducer = createReducer(
  initialSupportState,

  on(SupportActions.supportInitSuccessAction, state => ({
    ...state,
    isInitialized: true,
  })),

  on(SupportActions.supportRequestLoadAction, state => ({
    ...state,
    dashboardData: undefined,
    supportRequestChanged: undefined,
  })),

  on(SupportActions.supportRequestLoadSuccessAction, (state, action) => ({
    ...state,
    dashboardData: action.supportRequests,
    lastUpdated: Date.now(),
  })),

  on(SupportActions.supportRequestLoadFailAction, state => ({
    ...state,
    dashboardData: null,
  })),

  on(SupportActions.supportRequestChangedAction, (state, action) => ({
    ...state,
    assignedSupportRequests: getUpdatedAssignedSupportRequests(state, action)
  })),

  on(SupportActions.supportRequestDashboardChangedAction, (state, action) => ({
    ...state,
    supportRequestChanged: null,
  })),

  on(SupportActions.loadAssignedSupportRequestSuccessAction, (state, action) => ({
    ...state,
    assignedSupportRequests: action.supportRequests
  })),

  on(SupportActions.loadCurrentUserIdAction, (state, action) => ({
    ...state,
    currentUserId: action.userId
  })),

  on(SupportActions.upsertSupportRequestNotificationAction, (state, action) => ({
    ...state,
    showingNotification: getNotificationState(state, action)
  })),

  on(SupportActions.dismissSupportRequestNotificationAction, (state, action) => ({
    ...state,
    showingNotification: {
      ...state.showingNotification,
      supportRequests: state.showingNotification.supportRequests.filter(sr => sr.id !== action.supportRequestId),
      ringingSupportRequestIds: state.showingNotification.ringingSupportRequestIds.filter(id => id !== action.supportRequestId),
    }
  })),

  on(SupportActions.stopRingingSupportRequestNotificationAction, (state, action) => ({
    ...state,
    showingNotification: {
      ...state.showingNotification,
      ringingSupportRequestIds: state.showingNotification.ringingSupportRequestIds.filter(id => id !== action.supportRequestId),
    }
  })),

  on(SupportActions.supportRequestFilterChangeAction, (state, action) => ({
    ...state,
    supportRequestFilterOption: action.filterOption
  })),

  on(SupportActions.supportRequestChartToggleChangeAction, (state, action) => ({
    ...state,
    supportRequestChartToggle: action.showChart
  })),
);

function getUpdatedAssignedSupportRequests(state: SupportState, action: { supportRequest: SupportRequest; }): SupportRequest[] {
  const newState = [...state.assignedSupportRequests];
  const existingItemIndex = newState.findIndex(sr => sr.id === action.supportRequest.id);
  if (existingItemIndex > -1) {
    newState[existingItemIndex] = action.supportRequest;
  } else if (action.supportRequest.techId === state.currentUserId) {
    newState.push(action.supportRequest);
  }

  return newState.filter(sr => sr.status === SupportRequestStatus.InProgress);
}

function getNotificationState(currentState: SupportState, action: { supportRequest: SupportRequest; }): ISupportNotification {
  const notificationState = {
    ...currentState.showingNotification,
    supportRequests: [...currentState.showingNotification.supportRequests],
    ringingSupportRequestIds: [...currentState.showingNotification.ringingSupportRequestIds],
  } as ISupportNotification;

  if (action.supportRequest.status === SupportRequestStatus.New) {
    notificationState.supportRequests.push(action.supportRequest);
    notificationState.ringingSupportRequestIds.push(action.supportRequest.id);
    return notificationState;
  }

  const existingItemIndex = notificationState.supportRequests.findIndex(sr => sr.id === action.supportRequest.id);
  if (existingItemIndex > -1) {
    notificationState.supportRequests.splice(existingItemIndex, 1);
  }

  const ringIndex = notificationState.ringingSupportRequestIds.findIndex(id => id === action.supportRequest.id);
  if (ringIndex > -1) {
    notificationState.ringingSupportRequestIds.splice(ringIndex, 1);
  }

  return notificationState;
}

